/**
 * Registro de Chasis — migración web de ares1010 (Registrar Articulos).
 *
 * Alta de vehículos/productos en inventario: chasis, marca/modelo (del
 * catálogo marcamodelo), color, costo, precio, mínimo, condición, sucursal
 * (libre solo con permiso versucursal; si no, fija a la del usuario) y
 * suplidor opcional (mismo maestro de CxP que usa el registro bulk). Año y
 * cilindraje aparecen según los flags de varinicial, igual que el escritorio.
 * En consignaciones el suplidor es el consignante y no se edita desde aquí.
 *
 * Al escribir un chasis existente se cargan sus datos: se muestra su estado
 * (incautado/vendido, préstamo activo) y se habilitan Modificar (permiso
 * modificar), Eliminar y Reingresar (permiso borrar del rol, sin excepción
 * por tipo de usuario).
 * Todas las reglas de negocio se re-validan en el backend (ares1010backend).
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { ocultaCostos } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { useMarcasEstandar, unirOpciones, useVistaPrevia } from "lib/marcasEstandar";
import { VistaPreviaMarca } from "lib/AvisoMarcasEstandar";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const filtrarChasis = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.chasis} ${o.sucursal || ""}`),
  limit: 60,
});

// Mismos caracteres que rechaza el escritorio en chasis y marca.
const CARACTERES_PROHIBIDOS = ["/", "@", "$", "#", "&", "'"];

const FORM_INICIAL = {
  marca: "",
  modelo: "",
  colore: "",
  costo: "",
  precio: "",
  mprecio: "",
  condicion: "Nuevo",
  sucursal: "",
  ano: "",
  cilindraje: "",
  // Número de máquina (motor), opcional; mismo campo que llena el bulk.
  maquina: "",
  // Id del suplidor en maestcta (opcional); mismo campo que llena el bulk.
  suplidor: "",
  // Multi-empresa: única pantalla donde se elige la empresa dueña del
  // chasis; el resto del sistema (facturación, recibos, NCF) la deriva.
  empresa: "1",
};

export default function RegistroChasis() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  // Rol con "ocultarcostos": el campo Costo se oculta y al editar se preserva
  // el costo ya guardado. Además NO puede dar de alta chasis: un alta sin
  // costo entraría en inventario con costo 0 y falsearía el costo de compra,
  // así que "Añadir" queda bloqueado (Modificar/Eliminar siguen según su rol).
  const sinCostos = ocultaCostos(controller.roleDetalles);

  // ---- Configuración (flags, permisos, catálogos) ----
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let activo = true;
    post("/api/ares1010_config", {}).then(({ ok, data }) => {
      if (!activo) return;
      if (ok) setConfig(data);
      else showAlert({ message: data.Error || "No se pudo cargar la configuración", color: "error" });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const permisos = config?.permisos || {};
  const esadmin = !!config?.esadmin;
  const versucursal = esadmin || permisos.versucursal === "S";
  const puedeModificar = permisos.modificar === "S";
  // Solo el flag borrar del rol: sin bypass por tipo admin (el backend
  // valida igual; esto solo oculta los botones a quien no puede usarlos).
  const puedeEliminar = permisos.borrar === "S";
  const usaAno = config?.anoproducto === "S";
  const usaCilindraje = config?.cilindraje === "S";
  const usaMultiempresa = !!config?.multiempresa;
  const empresas = config?.empresas || [];
  const sucursales = config?.sucursales || [];
  const suplidores = config?.suplidores || [];

  // Marcas/modelos estándar del diccionario compartido (switch
  // "Estandarizar marcas y modelos"): se suman a las del catálogo propio.
  const estandar = useMarcasEstandar();
  const marcas = useMemo(
    () => unirOpciones(estandar.marcas, (config?.marcamodelo || []).map((m) => m.marca)),
    [config, estandar.marcas]
  );

  // ---- Chasis para el autocomplete ----
  const [opcionesChasis, setOpcionesChasis] = useState([]);
  const cargarChasis = useCallback(() => {
    post("/api/ares1010_chasislist", {}).then(({ ok, data }) => {
      if (ok) setOpcionesChasis(data.chasis || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(cargarChasis, [cargarChasis]);

  // ---- Formulario ----
  const [chasis, setChasis] = useState("");
  const [form, setForm] = useState(FORM_INICIAL);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Sucursal por defecto: la del usuario.
  useEffect(() => {
    if (!config) return;
    setForm((p) => (p.sucursal ? p : { ...p, sucursal: config.sucursal || "" }));
  }, [config]);

  const modelos = useMemo(() => {
    const lista = (config?.marcamodelo || []).filter(
      (m) => !form.marca || m.marca === form.marca
    );
    return unirOpciones(estandar.modelosDe(form.marca), lista.map((m) => m.modelo));
  }, [config, form.marca, estandar]);
  // "Se guardará como ...": lo que el backend va a guardar si difiere de lo digitado.
  const vistaMarca = useVistaPrevia(estandar.resolver, estandar.activo, form.marca, form.modelo);
  // "¿Quisiste decir ...? Usar": cambiar la marca desde aquí no debe vaciar
  // el modelo (el Autocomplete de marca lo limpia al cambiar su valor).
  const usandoSugerencia = useRef(false);
  const usarSugerencia = (sug) => {
    usandoSugerencia.current = true;
    setForm((p) => ({ ...p, marca: sug.marca, modelo: sug.modelo, ...preciosDeCatalogo(sug.marca, sug.modelo) }));
  };

  // ---- Chasis existente cargado ----
  const [existente, setExistente] = useState(null); // producto del backend o null
  const [buscando, setBuscando] = useState(false);

  // Catálogo de precios (Configuración → Catálogos): al escoger un modelo del
  // catálogo en un chasis nuevo, precio y mínimo se prellenan; siguen
  // editables para ajustar por unidad. Un chasis existente conserva lo suyo.
  const preciosDeCatalogo = (marca, modelo) => {
    if (existente) return {};
    const cat = (config?.marcamodelo || []).find(
      (m) => (m.marca || "").toUpperCase() === marca && (m.modelo || "").toUpperCase() === modelo
    );
    if (!cat) return {};
    const out = {};
    if (Number(cat.precio) > 0) out.precio = String(cat.precio);
    if (Number(cat.mprecio) > 0) out.mprecio = String(cat.mprecio);
    return out;
  };

  const limpiar = () => {
    setChasis("");
    setExistente(null);
    setForm({ ...FORM_INICIAL, sucursal: config?.sucursal || "" });
  };

  const buscarChasis = async (valor) => {
    const numero = String(valor || "")
      .trim()
      .toUpperCase()
      .replace(/[*']/g, "-");
    if (!numero) return;
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/ares1010_buscar", { chasis: numero });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo consultar el chasis", color: "error" });
        setChasis("");
        return;
      }
      if (!data.existe) {
        setExistente(null);
        return;
      }
      const p = data.producto;
      setExistente(p);
      setChasis(p.chasis);
      setForm({
        marca: p.marca,
        modelo: p.modelo,
        colore: p.colore,
        costo: p.costo,
        precio: p.precio,
        mprecio: p.mprecio,
        condicion: p.condicion || "Nuevo",
        sucursal: p.sucursal,
        ano: p.ano ? String(p.ano) : "",
        cilindraje: p.cilindraje || "",
        maquina: p.maquina || "",
        suplidor: String(p.suplidor || ""),
        empresa: String(p.empresa || 1),
      });
    } finally {
      setBuscando(false);
    }
  };

  // Validaciones del escritorio antes de llamar al backend.
  const validar = (paraInsertar) => {
    const num = String(chasis || "").trim();
    if (!num) return "El vehiculo/producto no puede estar en blanco";
    if (paraInsertar && num.length > 20)
      return "La maxima longitud para un numero de vehiculo/producto debe ser maximo 20";
    for (const c of CARACTERES_PROHIBIDOS) {
      if (num.includes(c)) return `No puede haber caracteres especiales (${c}) en el chasis. Revise`;
      if (form.marca.includes(c)) return `No puede haber caracteres especiales (${c}) en la marca. Revise`;
    }
    if (!form.marca.trim()) return "La marca no puede estar en blanco";
    if (!form.modelo.trim()) return "El modelo no puede estar en blanco";
    if (!form.colore.trim()) return "El color no puede estar en blanco";
    if (paraInsertar && sinCostos) return "Su rol no ve costos y no puede registrar chasis";
    if (!sinCostos && !String(form.costo).trim()) return "El costo no puede estar en blanco";
    if (paraInsertar && !(parseFloat(form.costo) > 0)) return "El costo debe ser mayor que 0";
    if (!String(form.precio).trim()) return "El precio no puede estar en blanco";
    if (!String(form.mprecio).trim()) return "El minimo no puede estar en blanco";
    if (!String(form.sucursal).trim()) return "La sucursal no puede estar en blanco. Revise";
    return null;
  };

  const [guardando, setGuardando] = useState(false);

  const enviar = async (endpoint, exito) => {
    setGuardando(true);
    try {
      const { ok, data } = await post(endpoint, {
        chasis: String(chasis).trim(),
        ...form,
        costo: String(form.costo).trim() === "" ? "0" : form.costo,
        sucursal: String(form.sucursal).split("-")[0].trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || exito, color: "success" });
        limpiar();
        cargarChasis();
      } else {
        showAlert({ message: data.Error || "No se pudo completar la operación", color: "error" });
      }
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const insertar = () => {
    if (sinCostos) {
      showAlert({
        message: "Su rol no puede agregar chasis al inventario porque no tiene acceso a los costos",
        color: "warning",
      });
      return;
    }
    const error = validar(true);
    if (error) {
      showAlert({ message: error, color: "warning" });
      return;
    }
    enviar("/api/ares1010_insertar", "Datos Insertados");
  };

  const modificar = () => {
    const error = validar(false);
    if (error) {
      showAlert({ message: error, color: "warning" });
      return;
    }
    enviar("/api/ares1010_modificar", "Datos modificados correctamente");
  };

  // Confirmaciones de eliminar/reingresar (el escritorio pregunta antes).
  const [confirmar, setConfirmar] = useState(null); // "eliminar" | "reingresar" | null

  const ejecutarConfirmada = () => {
    const accion = confirmar;
    setConfirmar(null);
    if (accion === "eliminar") enviar("/api/ares1010_eliminar", "Vehiculo/producto eliminado");
    if (accion === "reingresar") enviar("/api/ares1010_reingresar", "Vehiculo/producto reingresado");
  };

  // Etiquetas de estado del chasis cargado (mismas del escritorio).
  const estadoChips = [];
  if (existente) {
    if (existente.estadochasis === "I")
      estadoChips.push({ label: "Estado: Incautado", color: "error" });
    if (existente.estadochasis === "V")
      estadoChips.push({ label: "Estado: Vendido", color: "warning" });
    if (existente.statusprestamo === "A")
      estadoChips.push({ label: `Préstamo activo · No. ${existente.noprest}`, color: "error" });
    if (existente.statusprestamo === "C")
      estadoChips.push({ label: "Préstamo cancelado", color: "default" });
    if (existente.consignado === "S")
      estadoChips.push({ label: "Consignación", color: "info" });
    if (!estadoChips.length) estadoChips.push({ label: "Disponible", color: "success" });
  }

  const cargando = !config;
  // Vendido: costo, precio y mínimo no se modifican (el backend también lo valida).
  const vendido = existente?.estadochasis === "V";
  const ayudaVendido = vendido ? "Chasis vendido" : undefined;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registro de Chasis"
        subtitle="Registra vehículos y productos en el inventario, consulta su estado y corrige sus datos"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Chasis + estado */}
        <Box sx={sectionBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarChasis}
                options={opcionesChasis}
                loading={buscando}
                value={chasis}
                disabled={cargando}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : o.chasis
                }
                onInputChange={(_, v, motivo) => {
                  if (motivo === "input") {
                    // El lector de barras con otro idioma de teclado manda el
                    // guion como "*" o "'"; ningún chasis los lleva.
                    setChasis(v.toUpperCase().replace(/[*']/g, "-"));
                    if (existente) setExistente(null);
                  }
                }}
                onChange={(_, v) => {
                  if (v) buscarChasis(typeof v === "string" ? v : v.chasis);
                }}
                onBlur={() => {
                  if (chasis && !existente) buscarChasis(chasis);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={`${o.chasis}-${o.sucursal}`}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>{o.chasis}</Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        Sucursal {o.sucursal}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehículo / Producto (chasis)"
                    inputProps={{ ...params.inputProps, maxLength: 21 }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {cargando && <CircularProgress size={22} />}
                {estadoChips.map((c) => (
                  <Chip key={c.label} label={c.label} color={c.color} size="small" sx={{ fontWeight: 700 }} />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Datos del producto */}
        <Box sx={sectionBox}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={marcas}
                value={form.marca}
                disabled={cargando}
                onInputChange={(_, v) => {
                  const conservarModelo = usandoSugerencia.current;
                  usandoSugerencia.current = false;
                  setForm((p) => ({
                    ...p,
                    marca: (v || "").toUpperCase(),
                    modelo: conservarModelo ? p.modelo : "",
                  }));
                }}
                renderInput={(params) => <TextField {...params} label="Marca" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={modelos}
                value={form.modelo}
                disabled={cargando}
                onInputChange={(_, v) =>
                  setForm((p) => {
                    const modelo = (v || "").toUpperCase();
                    return { ...p, modelo, ...preciosDeCatalogo(p.marca, modelo) };
                  })
                }
                renderInput={(params) => <TextField {...params} label="Modelo" sx={fpInputSx} />}
              />
            </Grid>
            {vistaMarca && (
              <Grid item xs={12} sx={{ pt: "4px !important" }}>
                <VistaPreviaMarca vista={vistaMarca} onUsar={usarSugerencia} />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Color"
                value={form.colore}
                onChange={(e) => setForm((p) => ({ ...p, colore: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 15 }}
                sx={fpInputSx}
              />
            </Grid>
            {!sinCostos && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Costo"
                  placeholder="0.00"
                  value={form.costo}
                  onChange={(e) => setForm((p) => ({ ...p, costo: e.target.value.replace(/[^0-9.]/g, "") }))}
                  disabled={vendido}
                  helperText={ayudaVendido}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Precio"
                placeholder="0.00"
                value={form.precio}
                onChange={(e) => setForm((p) => ({ ...p, precio: e.target.value.replace(/[^0-9.]/g, "") }))}
                disabled={vendido}
                helperText={ayudaVendido}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Mínimo"
                placeholder="0.00"
                value={form.mprecio}
                onChange={(e) => setForm((p) => ({ ...p, mprecio: e.target.value.replace(/[^0-9.]/g, "") }))}
                disabled={vendido}
                helperText={ayudaVendido}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField select fullWidth label="Condición" value={form.condicion} onChange={setF("condicion")} sx={fpInputSx}>
                <MenuItem value="Nuevo">Nuevo</MenuItem>
                <MenuItem value="Usado">Usado</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {versucursal ? (
                <TextField select fullWidth label="Sucursal" value={form.sucursal} onChange={setF("sucursal")} sx={fpInputSx}>
                  {sucursales.map((s) => (
                    <MenuItem key={s.sucursal} value={s.sucursal}>
                      {s.sucursal} — {s.descripcion}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  disabled
                  label="Sucursal"
                  value={
                    sucursales.find((s) => s.sucursal === String(form.sucursal))
                      ? `${form.sucursal} — ${sucursales.find((s) => s.sucursal === String(form.sucursal)).descripcion}`
                      : form.sucursal
                  }
                  sx={fpInputSx}
                />
              )}
            </Grid>
            {usaAno && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Año"
                  value={form.ano}
                  onChange={(e) => setForm((p) => ({ ...p, ano: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) }))}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            {usaCilindraje && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Cilindraje"
                  value={form.cilindraje}
                  onChange={setF("cilindraje")}
                  inputProps={{ maxLength: 45 }}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="No. Máquina (opcional)"
                value={form.maquina}
                onChange={(e) => setForm((p) => ({ ...p, maquina: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 45 }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={suplidores}
                disabled={cargando || existente?.consignado === "S"}
                value={suplidores.find((s) => String(s.id) === String(form.suplidor)) || null}
                onChange={(_, v) => setForm((p) => ({ ...p, suplidor: v ? String(v.id) : "" }))}
                getOptionLabel={(o) => o.descrip || String(o.id)}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                renderInput={(params) => (
                  <TextField {...params} label="Suplidor (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            {usaMultiempresa && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField select fullWidth label="Empresa" value={form.empresa} onChange={setF("empresa")} sx={fpInputSx}>
                  {empresas.map((e) => (
                    <MenuItem key={e.empresa} value={String(e.empresa)}>
                      {e.empresa} — {e.ncomp || `Empresa ${e.empresa}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>

          <Stack direction="row" spacing={1.2} mt={2.5} flexWrap="wrap" useFlexGap>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={cargando || guardando || !!existente || sinCostos}
              onClick={insertar}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Añadir
            </Button>
            <Button
              startIcon={<SaveIcon />}
              disabled={cargando || guardando || !existente || !puedeModificar}
              onClick={modificar}
              sx={btnOutline(FP.color.navy)}
            >
              Modificar
            </Button>
            {puedeEliminar && (
              <>
                <Button
                  startIcon={<DeleteOutlineIcon />}
                  disabled={cargando || guardando || !existente}
                  onClick={() => setConfirmar("eliminar")}
                  sx={btnOutline("#D32F2F")}
                >
                  Eliminar
                </Button>
                <Button
                  startIcon={<ReplayIcon />}
                  disabled={cargando || guardando || !existente}
                  onClick={() => setConfirmar("reingresar")}
                  sx={btnOutline(FP.color.accentBlue)}
                >
                  Reingresar
                </Button>
              </>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.textSecondary)}>
              Limpiar
            </Button>
          </Stack>

          {sinCostos && (
            <Typography sx={{ mt: 1.5, fontSize: "0.78rem", color: FP.color.textMuted }}>
              <TwoWheelerIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
              Su rol no tiene acceso a los costos, por lo que no puede agregar chasis al
              inventario.
            </Typography>
          )}

          {existente && (
            <Typography sx={{ mt: 1.5, fontSize: "0.78rem", color: FP.color.textMuted }}>
              <TwoWheelerIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
              Chasis ya registrado en {existente.descsucursal || existente.sucursal}. Añadir está
              deshabilitado; use Modificar para corregir los datos.
            </Typography>
          )}
        </Box>
      </Box>

      {/* Confirmación de eliminar / reingresar */}
      <Dialog open={!!confirmar} onClose={() => setConfirmar(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {confirmar === "eliminar" ? "Eliminar chasis" : "Reingresar chasis"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmar === "eliminar"
              ? `¿Desea eliminar definitivamente el chasis ${chasis}? Solo es posible si nunca fue facturado ni tiene inicial.`
              : existente?.estadochasis === "V"
              ? `Este vehículo ya fue facturado. ¿Desea reingresar el chasis ${chasis} para poder facturarlo de nuevo?`
              : `¿Desea reingresar el chasis ${chasis}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmar(null)} sx={btnOutline(FP.color.textSecondary)}>
            No
          </Button>
          <Button onClick={ejecutarConfirmada} sx={btnSolid(confirmar === "eliminar" ? "#D32F2F" : FP.color.navy, confirmar === "eliminar" ? "#B71C1C" : FP.color.navySoft)}>
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
